import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, DollarSign, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const FeesTab = () => {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    fee_type: 'enrollment',
    amount: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchFees();
  }, []);

  const fetchFees = async () => {
    try {
      const response = await axios.get(`${API}/fees`);
      setFees(response.data);
    } catch (error) {
      toast.error('Failed to fetch fees');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        name: formData.name,
        fee_type: formData.fee_type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        is_active: formData.is_active
      };

      if (editingFee) {
        await axios.put(`${API}/fees/${editingFee.id}`, payload);
        toast.success('Fee updated successfully');
      } else {
        await axios.post(`${API}/fees`, payload);
        toast.success('Fee created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchFees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fee');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    setFormData({
      name: fee.name,
      fee_type: fee.fee_type,
      amount: fee.amount.toString(),
      description: fee.description,
      is_active: fee.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (feeId, feeName) => {
    if (!window.confirm(`Are you sure you want to delete "${feeName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/fees/${feeId}`);
      toast.success('Fee deleted successfully');
      fetchFees();
    } catch (error) {
      toast.error('Failed to delete fee');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      fee_type: 'enrollment',
      amount: '',
      description: '',
      is_active: true
    });
    setEditingFee(null);
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div data-testid="fees-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Service Fees</h1>
          <p className="text-gray-600 mt-1">Manage membership fees and charges</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-fee-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Fee
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingFee ? 'Edit Fee' : 'Create New Fee'}</DialogTitle>
              <DialogDescription>
                {editingFee ? 'Update fee details' : 'Add a new fee to your clinic'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Fee Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Enrollment Fee"
                  required
                  data-testid="fee-name-input"
                />
              </div>

              <div>
                <Label htmlFor="fee_type">Fee Type *</Label>
                <select
                  id="fee_type"
                  name="fee_type"
                  value={formData.fee_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  data-testid="fee-type-select"
                >
                  <option value="enrollment">Enrollment</option>
                  <option value="service">Service</option>
                  <option value="late_payment">Late Payment</option>
                  <option value="cancellation">Cancellation</option>
                  <option value="processing">Processing</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <Label htmlFor="amount">Amount ($) *</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                  data-testid="fee-amount-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of the fee"
                  rows={3}
                  data-testid="fee-description-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-teal-600 rounded"
                  data-testid="fee-active-checkbox"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Active (fees will be applied to transactions)
                </Label>
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="fee-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <DollarSign className="h-4 w-4 mr-2" />
                    {editingFee ? 'Update Fee' : 'Create Fee'}
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6 border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-blue-900">About Service Fees</CardTitle>
          <CardDescription className="text-blue-700">
            Service fees are additional charges applied to membership transactions. Common fees include enrollment fees, late payment penalties, and service charges. Fees marked as "Active" will automatically apply to relevant transactions.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Fees</CardTitle>
          <CardDescription>
            {fees.length === 0 ? 'No fees configured yet' : `${fees.length} fee(s) configured`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fees.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Service Fees</h3>
              <p className="text-gray-600 mb-4">
                Get started by adding your first service fee
              </p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Fee
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fees.map((fee) => (
                  <TableRow key={fee.id} data-testid={`fee-row-${fee.id}`}>
                    <TableCell className="font-medium">{fee.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {fee.fee_type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-bold text-teal-600">
                      ${fee.amount.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {fee.description || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={fee.is_active ? 'default' : 'secondary'}>
                        {fee.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(fee)}
                          data-testid={`edit-fee-${fee.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(fee.id, fee.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-fee-${fee.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export default FeesTab;
